// Copyright (c) StrangeBee 2024

import React, { ReactElement, useState } from 'react';
import { Store } from 'antd/lib/form/interface';
import { convertToLocaleId, useLocalMessage } from '@Helpers/';
import {
    PAPValueArray,
    PAPValues,
    SeverityValueArray,
    SeverityValues,
    TLPValueArray,
    TLPValues,
    CustomStatusMapType,
    AlertStatusStage,
} from 'thehive-sdk';
import { FormInstance } from 'antd/lib/form';
import { BulkEditFormField } from '@Molecules/BulkEditFormField/BulkEditFormField';
import { SecurityInput } from '@Atoms/Inputs/SecurityInput/SecurityInput';
import { THForm } from '@Molecules/THForm/THForm';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { Select } from 'antd';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { onOrganisationImageUpload } from '@Helpers/download';
import { IconLabel } from '@Atoms/Info';
import UserAvatar from '@Atoms/UserAvatar/UserAvatar';
import { useListAllUsers } from '@Queries/userQueries';

const { OptGroup } = Select;

export interface AlertBulkEditValues extends Store {
    tlp: TLPValues;
    pap: PAPValues;
    severity: SeverityValues;
    addTags: string[];
    removeTags: string[];
}

interface AlertBulkEditFormProps {
    onFinish: (values: AlertBulkEditValues) => void;
    form: FormInstance;
    isValid: boolean;
    onChange: () => void;
    changeStatus?: boolean;
    statusList?: CustomStatusMapType;
    onCancel: () => void;
}

export const AlertBulkEditForm = ({
    onFinish,
    form,
    isValid,
    onChange,
    changeStatus = false,
    statusList = {},
    onCancel,
}: AlertBulkEditFormProps): ReactElement => {
    const i = useLocalMessage();
    const [stage, setStage] = useState<string>();
    const statusDict = Object.values(statusList).flat();

    const onValuesChange = (changedValue: Store): void => {
        const valueName = Object.keys(changedValue)[0] || '';

        if (!valueName.includes('Checked')) {
            form.setFieldsValue({ [`${valueName}Checked`]: true });
        }
        onChange();
    };

    const onChangeStatus = (status: string): void => {
        const stage = (statusDict.find(({ value }) => value === status) || { stage: '-' }).stage;
        setStage(stage);
    };
    const { data: assignableUsers = [] } = useListAllUsers();

    return (
        <THForm<AlertBulkEditValues>
            name="alert-bulk-edit-form"
            onValuesChange={onValuesChange}
            form={form}
            onFinish={onFinish}
            initialValues={{
                pap: 2,
                tlp: 2,
                severity: 2,
            }}
        >
            <THForm.Layout
                footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} footerText={i('general.confirm')} />}
            >
                <BulkEditFormField
                    label={i('alert.tlp')}
                    name="tlp"
                    input={<SecurityInput type="tlp" valueArray={TLPValueArray} />}
                />

                <BulkEditFormField
                    label={i('alert.pap')}
                    name="pap"
                    input={<SecurityInput type="pap" valueArray={PAPValueArray} />}
                />

                <BulkEditFormField
                    label={i('alert.severity')}
                    name="severity"
                    input={<SecurityInput type="severity" hasLabel={false} valueArray={SeverityValueArray} />}
                />

                <BulkEditFormField
                    label={i('alert.bulk-edit.form.add-tags.label')}
                    name="addTags"
                    input={<THTagSelector.Autocomplete />}
                />

                <BulkEditFormField
                    label={i('alert.bulk-edit.form.remove-tags.label')}
                    name="removeTags"
                    input={<THTagSelector.Autocomplete />}
                />

                {changeStatus && (
                    <BulkEditFormField
                        name="status"
                        label={i('common.status')}
                        input={
                            <THSelect onChange={onChangeStatus}>
                                {Object.entries(statusList).map(([key, values]) => (
                                    <OptGroup key={key} label={key}>
                                        {values.map(({ value, colour }) => (
                                            <THSelect.Option key={value} value={value}>
                                                <CustomColorLabel
                                                    value={i({
                                                        id: `alert.data.status.${convertToLocaleId(value)}`,
                                                        defaultMessage: value,
                                                    })}
                                                    color={colour}
                                                />
                                            </THSelect.Option>
                                        ))}
                                    </OptGroup>
                                ))}
                            </THSelect>
                        }
                    />
                )}

                <BulkEditFormField
                    label={i('alert.change-status.form.summary.label')}
                    name="summary"
                    hidden={stage !== AlertStatusStage.CLOSED}
                    input={<THMarkdown sameSize onImageUpload={onOrganisationImageUpload} />}
                />

                <BulkEditFormField
                    label={i('task.create.form.assignee.label')}
                    name="assignee"
                    input={
                        <THSelect allowClear borderless placeholder={i('task.create.form.assignee.placeholder')}>
                            {assignableUsers?.map(({ _id, name, avatar, login }) => (
                                <THSelect.Option key={_id} value={login}>
                                    <IconLabel
                                        key={_id}
                                        icon={<UserAvatar name={name} avatar={avatar} />}
                                        label={name}
                                    />
                                </THSelect.Option>
                            ))}
                        </THSelect>
                    }
                />
            </THForm.Layout>
        </THForm>
    );
};
